import {
  ChooseType,
  Facility,
  HgSupportedLanguage,
  NumberRange,
  ObjectType,
  SearchModel,
} from '@smg-real-estate/hg-search-libs';
import { ComposerTranslation } from 'vue-i18n';

import { isPlatformIs24 } from '@/helpers/platformDetection';
import { useGeoStore } from '@/store/modules/geo';

const KEY_PREFIX = 'resultsList.heading';

/* eslint-disable-next-line max-params */
export function generateCategoryString(
  t: ComposerTranslation,
  chooseType: ChooseType | undefined,
  offerType: string | undefined,
  objectTypes: string[],
  facilities: Facility[] | undefined,
  roomsRange: NumberRange | undefined,
  keyPrefix = KEY_PREFIX,
): string {
  const objectTypeKeys = Object.keys(ObjectType);

  if (objectTypes !== undefined && objectTypes.length >= 1) {
    const categoryStrings = objectTypes
      .map((x) => {
        const objectType = objectTypeKeys.find(
          (key) => ObjectType[key as keyof typeof ObjectType] === x,
        );
        return objectType && t(`listingCategories.${objectType}`);
      })
      .filter((x) => x);

    if (categoryStrings.length > 0) {
      const offerTypeString =
        offerType && ' '.concat(t(`resultlist.offerType.${offerType}`).toString());
      return [categoryStrings.join(', '), offerTypeString].join('');
    }
  }

  if (facilities !== undefined && facilities.length >= 1) {
    const categoryStrings = facilities
      .map((facility) => facility && t(`searchBar.${facility}`))
      .filter((x) => x);

    if (categoryStrings.length > 0) {
      const offerTypeString =
        offerType && ' '.concat(t(`resultlist.offerType.${offerType}`).toString());
      return [categoryStrings.join(', '), offerTypeString].join('');
    }
  }

  if (
    !isPlatformIs24() &&
    !!roomsRange &&
    isNumberOfRoomsValidForCanonicalUrl(roomsRange, chooseType)
  ) {
    const chooseTypeString = chooseType && t(`resultsList.heading.${chooseType}`).toString();
    const roomsString = t(`metaInfo.srp.rooms.${roomsRange.from}RoomApartment`);

    return [chooseTypeString, roomsString].join(' ');
  }

  return t(`${keyPrefix}.${chooseType}`);
}

export function generateSearchHeading(
  { chooseType, objectTypes, offerType, locations, facilitiesRequired, numberOfRooms }: SearchModel,
  t: ComposerTranslation,
  geoStore: ReturnType<typeof useGeoStore>,
  locale: HgSupportedLanguage,
  resultCount?: number | undefined,
): string {
  let titleKey: string;

  if (locations.length && resultCount) {
    titleKey = 'title';
  } else if (locations.length) {
    titleKey = 'noResultTitle';
  } else {
    titleKey = 'titleWithoutLocation';
  }

  const titleTemplateAccordingToLocations = `resultsList.${titleKey}`;

  const isNewBuilding = isNewBuildingFacility(chooseType, facilitiesRequired);
  const categoryString = isNewBuilding
    ? t(offerType === 'rent' ? `${KEY_PREFIX}.rentnewbuilding` : `${KEY_PREFIX}.purchnewbuilding`)
    : generateCategoryString(t, chooseType, offerType, objectTypes, undefined, numberOfRooms);
  const [categoryPart1, categoryPart2] = splitCategoryString(
    categoryString.toString(),
    locale,
    isNewBuilding,
    chooseType,
  );

  return t(titleTemplateAccordingToLocations, {
    category: categoryString,
    categoryPart1: categoryPart1,
    loc: locations.map((locationId) => geoStore.getGeoLocationName(locationId, locale)).join(', '),
    categoryPart2: categoryPart2,
  });
}

function isNumberOfRoomsValidForCanonicalUrl(
  numberOfRooms: NumberRange | undefined,
  chooseType: ChooseType | undefined,
) {
  if (!numberOfRooms || !isValidRoomChooseType(chooseType)) {
    return false;
  }

  const { from, to } = numberOfRooms;

  // If from is undefined, we don't want to use it for canonical URL
  if (!from) {
    return false;
  }

  // HG only accepts integer values for from, but IS24 accepts float values (1.5, 2.5, etc.)
  const isFromValidHgInteger = from % 1 === 0;

  const isFromNumberValid = from < 6 && (!isPlatformIs24() ? isFromValidHgInteger : true);

  return isFromNumberValid && ((to && to - from === 0.5) || (!to && from === 5));
}

function isValidRoomChooseType(chooseType: ChooseType | undefined) {
  return (
    chooseType &&
    [
      ChooseType.rentflatonly,
      ChooseType.rentflat,
      ChooseType.purchflat,
      ChooseType.purchfaofh,
    ].includes(chooseType)
  );
}

export function splitCategoryString(
  categoryString: string,
  locale: string,
  isNewBuilding: boolean,
  chooseType?: ChooseType,
): [string, string] {
  let categoryPart1: string, categoryPart2: string;

  if (
    !isNewBuilding &&
    chooseType &&
    locale === HgSupportedLanguage.DE &&
    [ChooseType.purchfaofh].includes(chooseType)
  ) {
    [categoryPart1, categoryPart2] = categoryString.split(' ');
    categoryPart2 = ' ' + categoryPart2;
  } else {
    categoryPart1 = categoryString;
    categoryPart2 = '';
  }

  return [categoryPart1, categoryPart2];
}

function isRealEstateChooseType(chooseType: ChooseType): boolean {
  return [ChooseType.rentflat, ChooseType.purchfaofh].includes(chooseType);
}

function isNewBuildingFacility(
  chooseType: ChooseType | undefined,
  facilitiesRequired: Facility[],
): boolean {
  return (
    !!chooseType &&
    isRealEstateChooseType(chooseType) &&
    facilitiesRequired.length === 1 &&
    facilitiesRequired[0] === Facility.NEWLY_BUILT
  );
}
